import { useEffect } from "react";
import { useParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "utils/redux/hooks";
import { useConfigCatClient } from "configcat-react";
import { setChatbotFullScreen } from "../Chatbot/slice";
import {
  selectCompletionData,
  selectLoginlessConversationCache,
  selectLoginlessWeeklyCheckInState,
} from "./slice";
import { selectGetConfigCatFlagsStatus } from "../Global/slice";
import { ChatLoadingState, LoginlessHeader } from "app/components/Chat";
import ChatbotContent from "../Chatbot/ChatbotContent";
import StatusView from "./StatusView";
import {
  loadLoginlessConversation,
  saveCachedLoginlessConversation,
} from "./helpers";
import { loadConfigCatFlags } from "utils/helperFunctions";

export default function LoginlessWeeklyCheckinView() {
  const { encryptedThreadId } = useParams<{ encryptedThreadId: string }>();
  const dispatch = useAppDispatch();
  const configCatClient = useConfigCatClient();
  const uiState = useAppSelector(selectLoginlessWeeklyCheckInState);
  const completionData = useAppSelector(selectCompletionData);
  const getConfigCatStatus = useAppSelector(selectGetConfigCatFlagsStatus);
  // Use Redux selector to get cached conversation data
  const cachedConversation = useAppSelector(
    selectLoginlessConversationCache(encryptedThreadId || "")
  );

  // Load ConfigCat flags once userAccountId is available from the conversation cache
  useEffect(() => {
    if (getConfigCatStatus !== "idle" || !cachedConversation?.userAccountId) {
      return;
    }
    loadConfigCatFlags(
      configCatClient,
      dispatch,
      `${cachedConversation.userAccountId}`,
      `${cachedConversation.companyAccountId ?? ""}`
    );
  }, [
    configCatClient,
    dispatch,
    getConfigCatStatus,
    cachedConversation?.userAccountId,
    cachedConversation?.companyAccountId,
  ]);

  // Handle cached data immediately when component mounts
  useEffect(() => {
    // Set chatbot to full screen mode for loginless weekly check-in
    dispatch(setChatbotFullScreen(true));

    // Check if data is already cached for this token in Redux
    if (cachedConversation) {
      saveCachedLoginlessConversation(dispatch, cachedConversation);
      return; // Exit early if using cache
    }

    loadLoginlessConversation(dispatch, encryptedThreadId, configCatClient);
  }, [dispatch, encryptedThreadId, cachedConversation, configCatClient]);

  // If token is expired, render the token expired view
  if (uiState === "tokenExpired") {
    return (
      <StatusView
        title="Link Expired"
        message="This manager check-in link has expired. Please use the most recent link sent to your email to access your check-in."
        buttonText="Back to Develop by Criteria"
        icon="triangle-exclamation"
        iconColor="yellow"
        threadId={cachedConversation?.threadId}
      />
    );
  }

  // If token is invalid, render the invalid token view
  if (uiState === "invalidToken") {
    return (
      <StatusView
        title="Invalid Link"
        message="This manager check-in link is invalid or corrupted. Please use the correct link sent to your email to access your check-in."
        buttonText="Back to Develop by Criteria"
        icon="times-circle"
        iconColor="red"
        threadId={cachedConversation?.threadId}
      />
    );
  }

  // If server error, render the server error view
  if (uiState === "serverError") {
    return (
      <StatusView
        title="Technical Difficulties"
        message="We're experiencing technical difficulties at the moment. Please try again later or contact support if the issue persists."
        buttonText="Back to Develop by Criteria"
        icon="exclamation-circle"
        iconColor="red"
        threadId={cachedConversation?.threadId}
      />
    );
  }

  // During loading, show only the loading indicator, text, and subtext
  if (uiState === "loading") {
    return (
      <div
        style={{
          backgroundColor: "#F5F7FC",
          position: "relative",
          height: "100vh",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <ChatLoadingState
          loadingMessage="Please wait, Coach Bo is loading..."
          subMessage="This will take less than a minute."
          showInput={false}
        />
      </div>
    );
  }

  return (
    <div
      style={{
        backgroundColor: "#F5F7FC",
        position: "relative",
        height: "100vh",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <LoginlessHeader threadId={cachedConversation?.threadId} />
      <div
        style={{
          padding: "20px",
          backgroundColor: "#F5F7FC",
          borderRadius: "4px",
          flex: 1,
          display: "flex",
          flexDirection: "column",
          minHeight: 0,
          overflow: "hidden",
        }}
      >
        <h2 style={{ paddingBottom: "10px", flexShrink: 0 }}>
          Manager Check-in
        </h2>
        <ChatbotContent isLoginless />
      </div>
    </div>
  );
}
